import React, { useEffect, useRef, useState } from 'react';
import './CarbonFootprint.css';
import { useNavigate } from 'react-router-dom';
import logo_agroinsider_color from '../../assets/logo-agroinsider-color.svg';
import logo_carbon_footprint from '../../assets/pegada_carbono.svg';
import information_icon from '../../assets/circle-information.svg';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

function CarbonFootprint({ generalInfo, farmType, animalProduction, fuelSpent, vegetalProduction, setReset, userInfo }) {
    const navigate = useNavigate(); // Para navegação programática
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const { t } = useTranslation();
    const [state, setState] = useState({
        totalEmissions: 0,
        vegetablesPercentage: 0,
        animalPercentage: 0,
        fuelPercentage: 0,
        graphData: []
    });
    const [sendEmail, setSendEmail] = useState(true);
    const COLORS = ['#FF8021', '#006600', '#FF0000'];
    const RADIAN = Math.PI / 180;

    const mountedRef = useRef(true); // Ref para controlar o estado de montagem do componente
    const pdfRef = useRef(null);

    const cattleEmissions = 2.1550;
    const sheepEmissions = 0.2270;
    const birdsEmissions = 0.3545;
    const rabbitsEmissions = 0.2882;
    const horsesEmissions = 0.8380;
    const cerealEmissions = 0.5024
    const cornEmissions = 1.7008;
    const vineyardEmissions = 0.0920;
    const almondEmissions = 0.3539;
    const riceEmissions = 0.6136;
    const vegetablesEmissions = 1.4920;
    const irrigatedPastureEmissions = 1.4962;
    const othersCulturesEmissions = 0.1322;
    const othersOrchardsEmissions = 0.3539;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    useEffect(() => {
        async function saveDataSendEmail(farmName, email, totalEmissions, animalProduction, vegetalProduction, fuelSpent) {
            try {
                let body = {
                    organizationName: farmName,
                    email: email,
                    totalEmissions: totalEmissions,
                    animalProduction: animalProduction,
                    plantProduction: vegetalProduction,
                    fuels: fuelSpent,
                    language: "pt-PT",
                }
                await fetch(`${apiBase}calculator`, {
                    method: 'POST',
                    body: JSON.stringify(body),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                setSendEmail(false);
            } catch (e) {
                console.log(e)
            }
        }

        async function getVegetablesEmissions() {
            try {
                if (vegetalProduction) {
                    const totalCerealEmissions = vegetalProduction.amount.cereal_amount * cerealEmissions;
                    const totalCornEmissions = vegetalProduction.amount.corn_amount * cornEmissions;
                    const totalRiceEmissions = vegetalProduction.amount.rice_amount * riceEmissions;
                    const totalAlmondEmissions = vegetalProduction.amount.almond_amount * almondEmissions;
                    const totalIrrigatedPastureEmissions = vegetalProduction.amount.irrigated_pasture_amount * irrigatedPastureEmissions;
                    const totalOthersCulturesEmissions = vegetalProduction.amount.others_cultures_amount * othersCulturesEmissions;
                    const totalOthersOrchardsEmissions = vegetalProduction.amount.others_orchards_amount * othersOrchardsEmissions;
                    const totalVegetablesEmissions = vegetalProduction.amount.vegetables_amount * vegetablesEmissions;
                    const totalVineyardEmissions = vegetalProduction.amount.vineyard_amount * vineyardEmissions;

                    const total = totalCerealEmissions + totalCornEmissions + totalRiceEmissions
                        + totalAlmondEmissions + totalIrrigatedPastureEmissions + totalOthersCulturesEmissions
                        + totalOthersOrchardsEmissions + totalVegetablesEmissions + totalVineyardEmissions;

                    return total;
                }

            } catch {
                // Verifica se o componente ainda está montado antes de atualizar o estado
                if (mountedRef.current) {
                    setState(null);
                }
            }
        }

        async function getAnimalProductionEmissions() {
            try {
                if (animalProduction) {
                    const totalCattleEmissions = animalProduction.cattle * cattleEmissions;
                    const totalSheepEmissions = animalProduction.sheep * sheepEmissions;
                    const totalBirdsEmissions = animalProduction.birds * birdsEmissions;
                    const totalRabbitsEmissions = animalProduction.rabbits * rabbitsEmissions;
                    const totalHorsesEmissions = animalProduction.horses * horsesEmissions;


                    const total = totalCattleEmissions + totalSheepEmissions + totalBirdsEmissions
                        + totalRabbitsEmissions + totalHorsesEmissions;
                    return total;
                }

            } catch {
                // Verifica se o componente ainda está montado antes de atualizar o estado
                if (mountedRef.current) {
                    setState(null);
                }
            }
        }

        async function getFuelEmissions() {
            try {
                if (fuelSpent) {
                    const totalDieselEmissions = (fuelSpent.diesel * 0.84 * 0.86 * 44 / 12) / 1000;
                    const totalGasolineEmissions = (fuelSpent.gasoline * 0.75 * 0.87 * 44 / 12) / 1000;
                    const total = totalDieselEmissions + totalGasolineEmissions;
                    return total;
                }

            } catch {
                // Verifica se o componente ainda está montado antes de atualizar o estado
                if (mountedRef.current) {
                    setState(null);
                }
            }
        }
        async function getTotalEmissions() {
            const vegetablesEmissionsTotal = await getVegetablesEmissions();
            const animalEmissionsTotal = await getAnimalProductionEmissions();
            const fuelEmissionsTotal = await getFuelEmissions();
            const total = vegetablesEmissionsTotal + animalEmissionsTotal + fuelEmissionsTotal;

            let vegetablesEmissionsPercent = ((vegetablesEmissionsTotal.toFixed(1) / t) * 100);
            let animalEmissionsPercent = ((animalEmissionsTotal.toFixed(1) / t) * 100);
            let fuelEmissionsPercent = ((fuelEmissionsTotal.toFixed(1) / t) * 100);

            const totalPercentage = vegetablesEmissionsPercent + animalEmissionsPercent + fuelEmissionsPercent;

            if (totalPercentage !== 100) {
                // Normaliza os valores para garantir 100%
                const normalizationFactor = 100 / totalPercentage;
                vegetablesEmissionsPercent *= normalizationFactor;
                animalEmissionsPercent *= normalizationFactor;
                fuelEmissionsPercent *= normalizationFactor;
            }

            setState({
                totalEmissions: total.toFixed(2),
                vegetablesPercentage: vegetablesEmissionsPercent.toFixed(1) || 0,
                animalPercentage: animalEmissionsPercent.toFixed(1) || 0,
                fuelPercentage: fuelEmissionsPercent.toFixed(1) || 0,
                graphData: [animalEmissionsTotal, vegetablesEmissionsTotal, fuelEmissionsTotal],
            });


            if (sendEmail) {
                saveDataSendEmail(generalInfo.farmName, userInfo.email, total, animalEmissionsTotal, vegetablesEmissionsTotal, fuelEmissionsTotal);
            }
        }
        getTotalEmissions();
    }, [generalInfo, farmType, animalProduction, fuelSpent, vegetalProduction]);

    const handleGoFirstPage = () => {
        setReset(true);
        navigate('/');
    }

    const handleAskCall = () => {
        navigate('/ask-call-form');
    }

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        if (percent === 0) {
            return null; // Não renderiza nada se a percentagem for 0
        }

        const radius = innerRadius + (outerRadius - innerRadius) * 1.1;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="black" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {`${(percent * 100).toFixed(1)}%`}
            </text>
        );
    };


    const renderLabelLine = ({ percent }) => {
        return percent > 0 ? true : false; // Retorna false se percent for 0
    };

    return (
        <>
            {state ? (
                <div className="app-container">
                    <div className='div-agroinsider-logo'>
                        <img src={logo_agroinsider_color} alt="AgroInsider Logo" className="logo" />
                    </div>
                    <div className='div-page-logo'>
                        <img className='img-page-logo' src={logo_carbon_footprint} alt="AgroInsider Logo" />
                    </div>
                    <div className='farm-profile-description'>
                        <span className='farm-profile-title'>{t('CarbonFootprint.title')}</span>
                    </div>
                    <div ref={pdfRef}>
                        <div className="carbon-composition">
                            <span className='carbon-composition-title'>{t("CarbonFootprint.EMISSIONS")}</span>
                            <div className="pie-chart">
                                <span className='emissions-number'>{(state.totalEmissions)}</span>
                            </div>
                            <div className="legend">
                                <div className="legend-item">
                                    <span className="legend-text">ton CO<sub>2</sub>eq</span>
                                </div>
                            </div>
                            <div className='legend-item-note'>
                                <img style={{ width: "25px" }} src={information_icon} alt="AgroInsider Logo" />
                                <span>{t("APROXIMATED_VALUES_INFORMATION")}</span>
                            </div>
                        </div>
                        {/* Carbon Footprint Composition */}
                        <div className="carbon-composition">
                            <span className="carbon-composition-title">Composição da sua pegada de carbono</span>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart width={400} height={400}>
                                    <Pie
                                        isAnimationActive={false}
                                        data={[
                                            { name: 'Animal', value: state.graphData[0] },
                                            { name: 'Vegetables', value: state.graphData[1] },
                                            { name: 'Fuel', value: state.graphData[2] },
                                        ]}
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        fill="#8884d8"
                                        label={renderCustomizedLabel}
                                        labelLine={renderLabelLine}
                                        dataKey="value"
                                    >
                                        {state.graphData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="legend">
                                <div className="legend-item">
                                    <span className="color-box" style={{ backgroundColor: '#FF8021' }}></span>
                                    <span>{t("CarbonFootprint.ANIMAL_PRODUCTION")}<br />{`(${state.graphData[0] ? state.graphData[0].toFixed(2) : 0}`} ton CO<sub>2</sub>eq)</span>
                                </div>
                                <div className="legend-item">
                                    <span className="color-box" style={{ backgroundColor: '#006600' }}></span>
                                    <span>{t("CarbonFootprint.VEGETAL_PRODUCTION")}<br />{`(${state.graphData[1] ? state.graphData[1].toFixed(2) : 0}`} ton CO<sub>2</sub>eq)</span>
                                </div>
                                <div className="legend-item">
                                    <span className="color-box" style={{ backgroundColor: '#FF0000' }}></span>
                                    <span>{t("CarbonFootprint.FUEL_PRODUCTION")}<br />{`(${state.graphData[2] ? state.graphData[2].toFixed(2) : 0}`} ton CO<sub>2</sub>eq)</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Action Buttons */}
                    <div className="actions">
                        <button onClick={() => handleAskCall()} className="consultation-button">Consultoria Personalizada</button>
                    </div>
                    <button onClick={() => handleGoFirstPage()} className='initial-page-button'>
                        <span className='arrow-icon'>←</span>
                        <span>{t('initial_page')}</span>
                    </button>
                </div>
            ) : (
                <>
                </>
            )}
        </>


    );
}

export default CarbonFootprint;
